import { OutlineSegment, FlatHole, BendCenterLine } from './unfold';

// ASCII DXF (R12 / AC1009) writer with proper LTYPE + LAYER tables.
// CUT layer = continuous, CENTER layer = dash-dot red.
// Outline segments (lines + arcs) are merged into closed POLYLINEs where possible,
// with bulge values for arcs. Bend tangent lines (BEND layer) are intentionally
// omitted: at typical bend allowances (~2mm) they overlap visually with the
// centerline in CAD viewers, so we export only the CENTER marker for the
// press-brake operator. Cutters ignore CENTER by convention.

interface Pt { x: number; y: number; }
const eps = 1e-3;
const keyPt = (p: Pt) => `${p.x.toFixed(3)},${p.y.toFixed(3)}`;

interface ChainStep {
  // Vertex at this step's start point. Bulge applies from this vertex to the NEXT vertex in the chain.
  x: number; y: number;
  // Bulge to next: 0 for line, ±tan(sweepAngle/4) for arc (signed by direction).
  bulge: number;
}

function arcBulge(seg: OutlineSegment, forward: boolean): number {
  if (!seg.arc) return 0;
  // Compute sweep angle |Δθ| from start→end going CCW; for a semicircle it's π.
  // Determine sign based on direction (CCW=+ in math, y up).
  const startA = Math.atan2(seg.y1 - seg.arc.cy, seg.x1 - seg.arc.cx);
  const endA = Math.atan2(seg.y2 - seg.arc.cy, seg.x2 - seg.arc.cx);
  let sweep = endA - startA;
  if (seg.arc.ccw) {
    while (sweep <= 0) sweep += 2 * Math.PI;
  } else {
    while (sweep >= 0) sweep -= 2 * Math.PI;
  }
  // sweep is signed; if traversing in reverse, flip sign.
  const eff = forward ? sweep : -sweep;
  return Math.tan(eff / 4);
}

function chainSegments(segs: OutlineSegment[]): ChainStep[][] {
  const remaining = segs.map((s, i) => ({ s, used: false, i }));
  const adj = new Map<string, number[]>();
  remaining.forEach((r, idx) => {
    const ka = keyPt({ x: r.s.x1, y: r.s.y1 });
    const kb = keyPt({ x: r.s.x2, y: r.s.y2 });
    if (!adj.has(ka)) adj.set(ka, []);
    if (!adj.has(kb)) adj.set(kb, []);
    adj.get(ka)!.push(idx);
    adj.get(kb)!.push(idx);
  });

  const chains: ChainStep[][] = [];

  for (let start = 0; start < remaining.length; start++) {
    if (remaining[start].used) continue;
    const seg = remaining[start].s;
    remaining[start].used = true;

    // Walk forward from seg's end
    const chain: ChainStep[] = [
      { x: seg.x1, y: seg.y1, bulge: arcBulge(seg, true) },
      { x: seg.x2, y: seg.y2, bulge: 0 },
    ];

    let cont = true;
    while (cont) {
      cont = false;
      const tail = chain[chain.length - 1];
      const nbrs = adj.get(keyPt(tail)) ?? [];
      for (const idx of nbrs) {
        if (remaining[idx].used) continue;
        const next = remaining[idx].s;
        if (Math.abs(next.x1 - tail.x) < eps && Math.abs(next.y1 - tail.y) < eps) {
          // Forward: extend tail with next.x2,y2 and assign bulge at the join vertex
          tail.bulge = arcBulge(next, true);
          chain.push({ x: next.x2, y: next.y2, bulge: 0 });
          remaining[idx].used = true; cont = true; break;
        }
        if (Math.abs(next.x2 - tail.x) < eps && Math.abs(next.y2 - tail.y) < eps) {
          // Reverse: traverse next from x2,y2 to x1,y1
          tail.bulge = arcBulge(next, false);
          chain.push({ x: next.x1, y: next.y1, bulge: 0 });
          remaining[idx].used = true; cont = true; break;
        }
      }
    }

    // Walk backward from chain head
    cont = true;
    while (cont) {
      cont = false;
      const head = chain[0];
      const nbrs = adj.get(keyPt(head)) ?? [];
      for (const idx of nbrs) {
        if (remaining[idx].used) continue;
        const next = remaining[idx].s;
        if (Math.abs(next.x2 - head.x) < eps && Math.abs(next.y2 - head.y) < eps) {
          // next ends at head: prepend next.x1,y1 → next.x2,y2 = head
          chain.unshift({ x: next.x1, y: next.y1, bulge: arcBulge(next, true) });
          remaining[idx].used = true; cont = true; break;
        }
        if (Math.abs(next.x1 - head.x) < eps && Math.abs(next.y1 - head.y) < eps) {
          // next starts at head: prepend traversed in reverse, x2,y2 → head
          chain.unshift({ x: next.x2, y: next.y2, bulge: arcBulge(next, false) });
          remaining[idx].used = true; cont = true; break;
        }
      }
    }

    chains.push(chain);
  }

  return chains;
}

export function exportDxf(
  segments: OutlineSegment[],
  holes: FlatHole[] = [],
  centerLines: BendCenterLine[] = [],
): string {
  const lines: string[] = [];
  const w = (code: number, value: string | number) => {
    lines.push(String(code));
    lines.push(String(value));
  };

  // HEADER
  w(0, 'SECTION');
  w(2, 'HEADER');
  w(9, '$ACADVER'); w(1, 'AC1009');
  w(9, '$INSUNITS'); w(70, 4);
  w(0, 'ENDSEC');

  // TABLES
  w(0, 'SECTION'); w(2, 'TABLES');

  w(0, 'TABLE'); w(2, 'LTYPE'); w(70, 2);
  w(0, 'LTYPE'); w(2, 'CONTINUOUS'); w(70, 0);
  w(3, 'Solid line'); w(72, 65); w(73, 0); w(40, 0.0);
  w(0, 'LTYPE'); w(2, 'DASHDOT'); w(70, 0);
  w(3, '__ . __ . __'); w(72, 65); w(73, 4); w(40, 9.0);
  w(49, 6.0); w(49, -1.5); w(49, 0.0); w(49, -1.5);
  w(0, 'ENDTAB');

  w(0, 'TABLE'); w(2, 'LAYER'); w(70, 2);
  w(0, 'LAYER'); w(2, 'CUT'); w(70, 0); w(62, 7); w(6, 'CONTINUOUS');
  w(0, 'LAYER'); w(2, 'CENTER'); w(70, 0); w(62, 1); w(6, 'DASHDOT');
  w(0, 'ENDTAB');

  w(0, 'ENDSEC');

  // ENTITIES
  w(0, 'SECTION'); w(2, 'ENTITIES');

  // CUT outline as polylines (with bulge for arcs)
  const cuts = segments.filter((s) => s.layer === 'CUT');
  const chains = chainSegments(cuts);
  for (const chain of chains) {
    if (chain.length < 2) continue;
    const closed =
      Math.abs(chain[0].x - chain[chain.length - 1].x) < eps &&
      Math.abs(chain[0].y - chain[chain.length - 1].y) < eps;
    const pts = closed ? chain.slice(0, -1) : chain;
    w(0, 'POLYLINE');
    w(8, 'CUT');
    w(66, 1);
    w(70, closed ? 1 : 0);
    w(10, 0); w(20, 0); w(30, 0);
    for (const p of pts) {
      w(0, 'VERTEX');
      w(8, 'CUT');
      w(10, p.x.toFixed(4));
      w(20, p.y.toFixed(4));
      w(30, '0.0');
      if (Math.abs(p.bulge) > 1e-9) {
        w(42, p.bulge.toFixed(6));
      }
    }
    w(0, 'SEQEND');
  }

  // CENTER lines (bend axis markers) on their own layer; cutters ignore CENTER by convention.
  for (const seg of centerLines) {
    w(0, 'LINE');
    w(8, 'CENTER');
    w(6, 'DASHDOT');
    w(10, seg.x1.toFixed(4));
    w(20, seg.y1.toFixed(4));
    w(30, '0.0');
    w(11, seg.x2.toFixed(4));
    w(21, seg.y2.toFixed(4));
    w(31, '0.0');
  }

  // Holes on the CUT layer: round → CIRCLE entity, slot → closed POLYLINE with bulge.
  for (const h of holes) {
    if (h.kind === 'round') {
      w(0, 'CIRCLE');
      w(8, 'CUT');
      w(10, h.x.toFixed(4));
      w(20, h.y.toFixed(4));
      w(30, '0.0');
      w(40, (h.diameter / 2).toFixed(4));
    } else {
      // Slot: 4 vertices (corners of straight section) with bulge=1 at the cap vertices.
      const r = h.diameter / 2;
      const halfStr = (h.length - h.diameter) / 2;
      const cs = Math.cos(h.rotation), sn = Math.sin(h.rotation);
      const at = (lx: number, ly: number) => ({ x: h.x + lx * cs - ly * sn, y: h.y + lx * sn + ly * cs });
      const v1 = at(-halfStr, -r);
      const v2 = at(halfStr, -r);
      const v3 = at(halfStr, r);
      const v4 = at(-halfStr, r);
      w(0, 'POLYLINE'); w(8, 'CUT'); w(66, 1); w(70, 1);
      w(10, 0); w(20, 0); w(30, 0);
      // Bulge at v2: arc from v2 to v3 (right cap, semicircle bulging +x). Bulge sign +1.
      w(0, 'VERTEX'); w(8, 'CUT'); w(10, v1.x.toFixed(4)); w(20, v1.y.toFixed(4)); w(30, '0.0');
      w(0, 'VERTEX'); w(8, 'CUT'); w(10, v2.x.toFixed(4)); w(20, v2.y.toFixed(4)); w(30, '0.0'); w(42, '1.0');
      w(0, 'VERTEX'); w(8, 'CUT'); w(10, v3.x.toFixed(4)); w(20, v3.y.toFixed(4)); w(30, '0.0');
      w(0, 'VERTEX'); w(8, 'CUT'); w(10, v4.x.toFixed(4)); w(20, v4.y.toFixed(4)); w(30, '0.0'); w(42, '1.0');
      w(0, 'SEQEND');
    }
  }

  w(0, 'ENDSEC');
  w(0, 'EOF');

  return lines.join('\n') + '\n';
}

export function downloadDxf(filename: string, content: string) {
  const blob = new Blob([content], { type: 'application/dxf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
